import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { mutate } from 'swr';
import { ArrowUpIcon } from '@heroicons/react/24/solid';
import { HeroBackground } from '@/components/HeroBackground';
import { TypewriterText } from '@/components/agent-wizard/TypewriterText';
import { Phase1BusinessAnalysis } from '@/components/agent-wizard/Phase1BusinessAnalysis';
import { Phase2CampaignSetup } from '@/components/agent-wizard/Phase2CampaignSetup';
import { Phase3Review } from '@/components/agent-wizard/Phase3Review';
import {
  agentWizardService,
  type Phase1Proposal,
  type Phase2Proposal,
} from '@/lib/services/agent-wizard';

type WizardState =
  | { step: 'url-input' }
  | { step: 'loading' }
  | { step: 'phase'; sessionId: string };

export default function WorkspaceSetup() {
  const navigate = useNavigate();

  const [state, setState] = useState<WizardState>({ step: 'url-input' });
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [currentPhase, setCurrentPhase] = useState(1);

  const [phase1Proposal, setPhase1Proposal] = useState<Phase1Proposal | null>(null);
  const [phase2Proposal, setPhase2Proposal] = useState<Phase2Proposal | null>(null);

  const [messages, setMessages] = useState<{ role: string; content: string }[]>([]);
  const [isDiscussing, setIsDiscussing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [activityFeed, setActivityFeed] = useState<string[]>([]);
  const [chatInput, setChatInput] = useState('');

  const [showDataPanels, setShowDataPanels] = useState(false);
  const [showChatMessages, setShowChatMessages] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);

  const normalizePhase1 = (p: any): Phase1Proposal => ({
    businessSummary: p?.businessSummary || '',
    offerings: Array.isArray(p?.offerings) ? p.offerings : [],
    icp: {
      title: p?.icp?.title || '',
      seniority: p?.icp?.seniority || '',
      industry: p?.icp?.industry || '',
      companySize: p?.icp?.companySize || '',
      geography: p?.icp?.geography || '',
      customFilters: Array.isArray(p?.icp?.customFilters) ? p.icp.customFilters : [],
    },
    reasoning: p?.reasoning || '',
  });

  const handleStartWithUrl = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!websiteUrl.trim()) return;

    setState({ step: 'loading' });
    setActivityFeed([]);

    const domain = websiteUrl.trim().replace('https://', '').replace('http://', '').replace('www.', '').split('/')[0];
    const progressSteps = [
      { text: `Visiting ${domain}`, delay: 1500 },
      { text: 'Reading the homepage', delay: 2800 },
      { text: 'Understanding value proposition', delay: 3400 },
      { text: 'Identifying target market', delay: 2800 },
      { text: 'Building business profile', delay: 3200 },
    ];
    let cancelled = false;
    const runProgress = async () => {
      for (const step of progressSteps) {
        if (cancelled) break;
        await new Promise(r => setTimeout(r, step.delay + (Math.random() - 0.5) * 800));
        if (cancelled) break;
        setActivityFeed(prev => [...prev, step.text]);
      }
    };
    runProgress();

    try {
      const result = await agentWizardService.start({ websiteUrl: websiteUrl.trim() });
      cancelled = true;
      setPhase1Proposal(normalizePhase1(result.proposal));
      setCurrentPhase(1);

      // Fire intro in parallel with transitions
      const introPromise = agentWizardService.sendIntro(result.sessionId);

      await new Promise(r => setTimeout(r, 800));
      setActivityFeed([]);
      setState({ step: 'phase', sessionId: result.sessionId });
      await new Promise(r => setTimeout(r, 600));
      setShowDataPanels(true);
      await new Promise(r => setTimeout(r, 400));
      setShowChatMessages(true);
      setIsDiscussing(true);
      await new Promise(r => setTimeout(r, 400));
      setShowChatInput(true);

      try {
        const introResult = await introPromise;
        setIsDiscussing(false);
        setMessages([{ role: 'assistant', content: introResult.reply }]);
      } catch {
        setIsDiscussing(false);
        setMessages([{ role: 'assistant', content: 'I\'ve put together a business profile. Take a look and let me know if anything needs changing.' }]);
      }
    } catch {
      toast.error('Failed to analyse website. Please try again.');
      setActivityFeed([]);
      setState({ step: 'url-input' });
    }
  };

  const handleSendMessage = useCallback(async (message: string) => {
    if (state.step !== 'phase' || !message.trim()) return;

    setMessages(prev => [...prev, { role: 'user', content: message }]);
    setIsDiscussing(true);

    try {
      const currentProposal = currentPhase === 1 ? phase1Proposal : phase2Proposal;
      const result = await agentWizardService.sendMessage(state.sessionId, message, currentProposal as any);

      if (result.proposal) {
        if (currentPhase === 1) setPhase1Proposal(normalizePhase1(result.proposal));
        else setPhase2Proposal(result.proposal as Phase2Proposal);
      }

      setIsDiscussing(false);
      if (result.reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: result.reply! }]);
      }
    } catch {
      setIsDiscussing(false);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong. Please try again.' }]);
    }
  }, [state, currentPhase, phase1Proposal, phase2Proposal]);

  const handleConfirm = useCallback(async () => {
    if (state.step !== 'phase') return;

    if (currentPhase === 3) {
      // Final step — save to workspace and complete
      setIsConfirming(true);
      try {
        const result = await agentWizardService.complete(state.sessionId);

        // Save workspace profile
        await fetch('/api/workspace', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            websiteUrl: websiteUrl || null,
            businessSummary: phase1Proposal?.businessSummary || null,
            offerings: phase1Proposal?.offerings || null,
            icp: phase1Proposal?.icp || null,
            outreachGoal: phase2Proposal?.outreachGoal || null,
            messagingTone: phase2Proposal?.messagingTone || null,
            workspaceSetupComplete: true,
          }),
        });

        // Revalidate team data so ProtectedRoute sees the update
        await mutate('/api/team');

        toast.success('AI context set up!');
        navigate(`/dashboard/campaigns/${result.campaignId}/edit`);
      } catch {
        toast.error('Failed to complete setup');
      } finally {
        setIsConfirming(false);
      }
      return;
    }

    const confirmedData = currentPhase === 1 ? phase1Proposal : phase2Proposal;
    if (!confirmedData) return;

    setIsConfirming(true);
    try {
      const result = await agentWizardService.confirmPhase(state.sessionId, confirmedData as unknown as Record<string, unknown>);
      const nextPhase = currentPhase + 1;
      setCurrentPhase(nextPhase);

      if (nextPhase === 2 && result.proposal) {
        setPhase2Proposal(result.proposal as Phase2Proposal);
      }

      setMessages([]);
      setIsDiscussing(true);

      try {
        const phaseIntro = await agentWizardService.sendIntro(state.sessionId);
        setIsDiscussing(false);
        setMessages([{ role: 'assistant', content: phaseIntro.reply }]);
      } catch {
        setIsDiscussing(false);
        setMessages([{ role: 'assistant', content: 'Review the details and let me know if you want any changes.' }]);
      }
    } catch {
      toast.error('Failed to proceed');
    } finally {
      setIsConfirming(false);
    }
  }, [state, currentPhase, phase1Proposal, phase2Proposal, websiteUrl, navigate]);

  // URL input screen
  if (state.step === 'url-input' || state.step === 'loading') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 w-full max-w-xl px-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-medium tracking-tight mb-2">Set up your AI context</h1>
            <p className="text-muted-foreground">Enter your website and we'll build your business profile automatically.</p>
          </div>

          {state.step === 'loading' && activityFeed.length > 0 ? (
            <div className="space-y-2 max-w-sm mx-auto">
              {activityFeed.map((text, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground animate-in fade-in slide-in-from-bottom-1">
                  <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0" />
                  {text}
                </div>
              ))}
            </div>
          ) : (
            <form onSubmit={handleStartWithUrl} className="flex gap-3">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="yourcompany.com"
                  className="w-full h-14 px-5 pr-14 rounded-full border border-control-border bg-input-bg shadow-lg text-base focus:outline-none focus:ring-2 focus:ring-primary/20"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={!websiteUrl.trim()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-primary text-on-accent flex items-center justify-center disabled:opacity-50 transition-opacity"
                >
                  <ArrowUpIcon className="h-5 w-5" />
                </button>
              </div>
            </form>
          )}

          <p className="text-center text-xs text-muted-foreground mt-4">
            <button type="button" className="underline" onClick={() => {
              // Skip workspace setup for now
              fetch('/api/workspace/setup-complete', { method: 'POST', credentials: 'include' })
                .then(() => mutate('/api/team'))
                .then(() => navigate('/dashboard/campaigns'));
            }}>
              Skip for now
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Phase view (business analysis → campaign setup → review)
  return (
    <div className="h-screen flex overflow-hidden">
      {/* Data panels */}
      <div className={`flex-1 overflow-y-auto p-6 transition-opacity duration-500 ${showDataPanels ? 'opacity-100' : 'opacity-0'}`}>
        {currentPhase === 1 && phase1Proposal && (
          <Phase1BusinessAnalysis
            proposal={phase1Proposal}
            onChange={setPhase1Proposal}
          />
        )}
        {currentPhase === 2 && phase2Proposal && (
          <Phase2CampaignSetup
            proposal={phase2Proposal}
            onChange={setPhase2Proposal}
          />
        )}
        {currentPhase === 3 && phase1Proposal && phase2Proposal && (
          <Phase3Review
            phase1={phase1Proposal}
            phase2={phase2Proposal}
          />
        )}
      </div>

      {/* Chat sidebar */}
      <div className="w-[400px] shrink-0 flex flex-col bg-sidebar border-l border-border">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-sm font-medium">AI Context Setup — Phase {currentPhase} of 3</h3>
          <button
            onClick={handleConfirm}
            disabled={isConfirming}
            className="px-4 py-2 bg-primary text-on-accent text-sm rounded-full disabled:opacity-50"
          >
            {isConfirming ? 'Processing...' : currentPhase === 3 ? 'Complete Setup' : 'Confirm & Continue'}
          </button>
        </div>

        <div className={`flex-1 overflow-y-auto px-5 py-4 space-y-4 transition-opacity duration-500 ${showChatMessages ? 'opacity-100' : 'opacity-0'}`}>
          {messages.map((msg, i) => (
            <div key={i} className={`text-sm ${msg.role === 'assistant' ? 'text-foreground' : 'text-muted-foreground bg-surface-sunken rounded-lg p-3'}`}>
              {msg.role === 'assistant' ? <TypewriterText text={msg.content} /> : msg.content}
            </div>
          ))}
          {isDiscussing && (
            <div className="flex gap-1.5 py-2">
              <div className="w-2 h-2 bg-surface-avatar rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 bg-surface-avatar rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 bg-surface-avatar rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          )}
        </div>

        {showChatInput && (
          <div className="px-5 py-4 border-t border-border">
            <form onSubmit={(e) => { e.preventDefault(); if (chatInput.trim()) { handleSendMessage(chatInput); setChatInput(''); } }}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask me to change anything..."
                  className="flex-1 h-10 px-4 rounded-full border border-control-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <button
                  type="submit"
                  disabled={!chatInput.trim() || isDiscussing}
                  className="h-10 w-10 rounded-full bg-primary text-on-accent flex items-center justify-center disabled:opacity-50"
                >
                  <ArrowUpIcon className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
